import { useMutation, useQuery } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import type {
  AlertBannerInteraction,
  FetchVisibleAlertBannersInput,
  FetchVisibleAlertBannersResponse,
  TransitionAlertBannerInteractionInput,
  TransitionAlertBannerInteractionResponse,
  UpsertAlertBannerInteractionsInput,
  UpsertAlertBannerInteractionsResponse,
} from '@generated/graphql';
import {
  AlertBannerInteractionTransitions,
  AlertBannerPlatform,
  PersistenceBehaviour,
} from '@generated/graphql';
import { Box } from '@ovComponents/1-primative/box/box';
import {
  FETCH_VISIBLE_ALERT_BANNERS,
  TRANSITION_ALERT_BANNER_INTERACTION,
  UPSERT_ALERT_BANNER_INTERACTION,
} from '@ovComponents/5-page/settings/alertBanners/alertBanners.queries';
import AlertBannerDisplay from '@ovComponents/5-page/settings/alertBanners/components/alertBannerDisplay';
import { useGlobalToast } from '@providers/globalToastProvider';
import { UserContext } from '@providers/userContextProvider';
import { safeWindowOpen } from '@utils/urlSanitization';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sortBannerInteractionsByType } from './utils';

const AlertBanner = () => {
  const { t } = useTranslation(['alertBanners']);
  const { showToast } = useGlobalToast();
  const { activeOrganization } = useContext(UserContext);
  const [bannerInteractions, setBannerInteractions] = useState<
    AlertBannerInteraction[]
  >([]);
  const [refetchIndex, setRefreshIndex] = useState(0);

  const organizationId = activeOrganization?.id;

  const { loading, data, refetch } = useQuery<
    { fetchVisibleAlertBanners: FetchVisibleAlertBannersResponse },
    { input: FetchVisibleAlertBannersInput }
  >(FETCH_VISIBLE_ALERT_BANNERS, {
    skip: !organizationId,
    variables: {
      input: {
        organizationId,
        platform: AlertBannerPlatform.Web,
      },
    },
    fetchPolicy: 'network-only',
  });

  const alertBannerIds = useMemo(
    () =>
      data?.fetchVisibleAlertBanners.visibleAlertBanners.map(
        (visibleBanner) => visibleBanner.id,
      ) ?? [],
    [data],
  );

  const [
    upsertAlertBannerInteractions,
    { loading: upsertInteractionsLoading },
  ] = useMutation<
    { upsertAlertBannerInteractions: UpsertAlertBannerInteractionsResponse },
    { input: UpsertAlertBannerInteractionsInput }
  >(UPSERT_ALERT_BANNER_INTERACTION, {
    fetchPolicy: 'no-cache',
    onCompleted: (upsertData) => {
      setBannerInteractions(
        upsertData.upsertAlertBannerInteractions.alertBannerInteractions,
      );
    },
  });

  const [transitionAlertBannerInteraction] = useMutation<
    {
      transitionAlertBannerInteraction: TransitionAlertBannerInteractionResponse;
    },
    {
      input: TransitionAlertBannerInteractionInput;
      skipErrorHandler: boolean;
    }
  >(TRANSITION_ALERT_BANNER_INTERACTION);

  useEffect(() => {
    if (!organizationId || !alertBannerIds.length) {
      setBannerInteractions([]);
      return;
    }

    upsertAlertBannerInteractions({
      variables: {
        input: {
          alertBannerIds,
          organizationId,
        },
      },
    });
  }, [
    upsertAlertBannerInteractions,
    alertBannerIds,
    organizationId,
    refetchIndex,
  ]);

  const removeBanner = (bannerInteraction: AlertBannerInteraction) => {
    setBannerInteractions((prev) =>
      prev.filter((x) => x.id !== bannerInteraction.id),
    );
  };

  const refetchVisibleBanners = async () => {
    await refetch();
    setRefreshIndex((idx) => idx + 1);
  };

  const handleAction = async (bannerInteraction: AlertBannerInteraction) => {
    if (!bannerInteraction.alertBanner.configuration?.enableAction) return;

    try {
      await transitionAlertBannerInteraction({
        variables: {
          input: {
            alertBannerInteractionId: bannerInteraction.id,
            transition: AlertBannerInteractionTransitions.Action,
          },
          skipErrorHandler: true,
        },
      });
      safeWindowOpen(bannerInteraction.alertBanner.actionButton?.url);

      if (
        bannerInteraction.alertBanner.persistenceBehaviour ===
        PersistenceBehaviour.UntilActioned
      ) {
        removeBanner(bannerInteraction);
      }
    } catch (e: unknown) {
      datadogLogs.logger.error('Alert banner action transition failed', {
        alertBannerInteractionId: bannerInteraction.id,
        error: e instanceof Error ? e.message : String(e),
      });
      showToast({
        severity: 'error',
        message: t('transitionError'),
      });
      removeBanner(bannerInteraction);
      await refetchVisibleBanners();
    }
  };

  const handleDismiss = async (bannerInteraction: AlertBannerInteraction) => {
    if (!bannerInteraction.alertBanner.configuration?.makeDismissible) return;

    try {
      await transitionAlertBannerInteraction({
        variables: {
          input: {
            alertBannerInteractionId: bannerInteraction.id,
            transition: AlertBannerInteractionTransitions.Dismiss,
          },
          skipErrorHandler: true,
        },
      });
    } catch (e: unknown) {
      datadogLogs.logger.error('Alert banner dismiss transition failed', {
        alertBannerInteractionId: bannerInteraction.id,
        error: e instanceof Error ? e.message : String(e),
      });
      showToast({
        severity: 'error',
        message: t('transitionError'),
      });
      await refetchVisibleBanners();
    }

    removeBanner(bannerInteraction);
  };

  const sortedBannerInteractions =
    sortBannerInteractionsByType(bannerInteractions);

  if (loading || upsertInteractionsLoading || !sortedBannerInteractions.length)
    return null;

  return (
    <Box display='flex' flexDirection='column' width='100%' gap={1} mb={2}>
      {sortedBannerInteractions.map((interaction) => (
        <AlertBannerDisplay
          key={interaction.id}
          bannerFields={interaction.alertBanner}
          onDismiss={() => handleDismiss(interaction)}
          onAction={() => handleAction(interaction)}
        />
      ))}
    </Box>
  );
};

export default AlertBanner;
